import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Copy, FileText, MessageCircle, Pencil, Plus, Send, Trash2, Upload, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, Empty, Field, Modal, Pill, StatCard, StudentPicker, btnCls, btnGhost, inputCls, waNumber } from "@/components/followup/ui";
import { GradePill, History, RatersTable, ROLE_NAMES, Violations, fmtWeek, letterOf, linkOf, requestMessage } from "@/components/followup/shared";
import ReportsList from "@/components/followup/Reports";

async function copyText(text) {
  try { await navigator.clipboard.writeText(text); toast.success("تم النسخ"); }
  catch { toast.error("تعذّر النسخ، انسخ النص يدويًا"); }
}

const norm = (x) => String(x || "").replace(/[\s/\\-]/g, "").toUpperCase();
const classTokens = (u) => String(u.classes || "").split(/[،,;]/).map(norm).filter(Boolean);

// المعلمون ورائد النشاط والوكيل الذين يستلمون الروابط
function RaterPicker({ contacts, teachers, setTeachers, activity, setActivity, deputies, setDeputies, children, hint }) {
  const tList = contacts.filter((u) => u.role === "teacher");
  const aList = contacts.filter((u) => u.role === "activity");
  const dList = contacts.filter((u) => u.role === "deputy");
  const toggle = (list, setList, id, on) => setList(on ? [...list, id] : list.filter((x) => x !== id));
  return (
    <div className="space-y-3">
      <div>
        <div className="text-sm mb-1.5">المعلمون الذين يدرّسون الطالب</div>
        {hint && <div className="text-xs text-[#4A554E] mb-2">{hint}</div>}
        {tList.length === 0 ? <Empty>أضف المعلمين أولاً من تبويب «المعلمون والوكيل والنشاط»</Empty> : (
          <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {tList.map((u) => (
              <label key={u.id} className="flex items-center gap-2 border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm">
                <input type="checkbox" checked={teachers.includes(u.id)} onChange={(e) => toggle(teachers, setTeachers, u.id, e.target.checked)} />
                <span className="flex-1 min-w-0">{u.name} <span className="block text-xs text-[#4A554E]">{u.subjects || "—"}{u.classes ? ` — ${u.classes}` : ""}</span></span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="text-sm mb-1.5">الوكيل</div>
        {dList.length === 0 ? <div className="text-xs text-[#4A554E]">لم يُضف وكيل بعد (تبويب «المعلمون والوكيل والنشاط»).</div> : (
          <div className="grid sm:grid-cols-2 gap-2">
            {dList.map((u) => (
              <label key={u.id} className="flex items-center gap-2 border border-[#e6e2d8] rounded-xl px-3 py-2 text-sm">
                <input type="checkbox" checked={deputies.includes(u.id)} onChange={(e) => toggle(deputies, setDeputies, u.id, e.target.checked)} /> {u.name}
              </label>
            ))}
          </div>
        )}
      </div>
      <Field label="رائد النشاط">
        <select className={inputCls} value={activity} onChange={(e) => setActivity(e.target.value)} data-testid="fu-activity">
          <option value="">بدون</option>
          {aList.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </Field>
      {children}
    </div>
  );
}

// ---------- طالب واحد تحت المتابعة هذا الأسبوع ----------
function CycleModal({ cycle, onClose, onChanged, contacts, counselorName }) {
  const [c, setC] = useState(cycle);
  const [mode, setMode] = useState(cycle.justCreated ? "send" : "view");
  const [teachers, setTeachers] = useState(cycle.teacher_ids || []);
  const [deputies, setDeputies] = useState(cycle.deputy_ids || []);
  const [activity, setActivity] = useState(cycle.activity_id || "");
  const reload = () => api.get(`/followup/cycles/${c.id}`).then((r) => setC(r.data));
  const markSent = async (rid) => { try { await api.post(`/followup/cycles/${c.id}/sent`, { recipient: rid }); reload(); } catch { /* تجاهل */ } };
  const close = async () => {
    if (!window.confirm("إغلاق متابعة هذا الأسبوع وإنشاء التقرير؟ لن يستطيع أحد التقييم بعدها.")) return;
    try { await api.post(`/followup/cycles/${c.id}/close`); toast.success("أُنشئ التقرير وأُرسل للمشرف الإرشادي للاعتماد"); onChanged(); onClose(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async () => {
    if (!window.confirm("حذف متابعة هذا الطالب لهذا الأسبوع؟")) return;
    try { await api.delete(`/followup/cycles/${c.id}`); onChanged(); onClose(); } catch (e) { toast.error(formatErr(e)); }
  };
  const saveRaters = async () => {
    try { const r = await api.put(`/followup/cycles/${c.id}`, { teacher_ids: teachers, deputy_ids: deputies, activity_id: activity || "" }); setC(r.data); setMode("send"); onChanged(); toast.success("تم الحفظ"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const s = c.student;
  const canManage = c.status === "active";
  return (
    <Modal title={`${s.name} — ${s.grade || ""}`} onClose={onClose} wide>
      <div className="space-y-4" data-testid="fu-cycle">
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="النسبة العامة" value={c.pct == null ? "—" : `${letterOf(c.pct)} · ${c.pct}%`} />
          <StatCard label="سلوكي" value={c.pct_b == null ? "—" : letterOf(c.pct_b)} />
          <StatCard label="تعليمي" value={c.pct_l == null ? "—" : letterOf(c.pct_l)} />
        </div>
        <div className="text-sm text-[#4A554E]">أسبوع {fmtWeek(c.week)}{c.reason && <> — سبب المتابعة: {c.reason}</>}</div>
        <div className="flex flex-wrap gap-2">
          {canManage && <button className={mode === "send" ? btnCls : btnGhost} onClick={() => setMode("send")} data-testid="fu-send-tab"><Send className="w-4 h-4" /> إرسال الروابط</button>}
          <button className={mode === "view" ? btnCls : btnGhost} onClick={() => setMode("view")}><ClipboardCheck className="w-4 h-4" /> استمارة التقييم</button>
          {canManage && <button className={mode === "raters" ? btnCls : btnGhost} onClick={() => setMode("raters")}><Pencil className="w-4 h-4" /> تعديل المقيّمين</button>}
        </div>

        {mode === "view" && <RatersTable raters={c.raters || []} />}

        {mode === "send" && (
          <div className="space-y-3" data-testid="fu-send">
            <div className="text-sm bg-[#F9F8F6] border border-[#e6e2d8] rounded-xl p-3">لكل مقيّم رابط خاص به لا يحتاج حسابًا. اضغط «واتساب» لتفتح الرسالة جاهزة على رقمه، أو انسخها. يستطيع المعلم التقييم بعد كل حصة، والوكيل وولي الأمر يوميًا، طوال الأسبوع، ثم تصلك التقييمات تلقائيًا هنا.</div>
            {(c.raters || []).map((r) => {
              const msg = requestMessage(c, r, counselorName);
              const sent = (c.sent || {})[r.id];
              return (
                <div key={`s-${r.role}-${r.id}`} className="border border-[#e6e2d8] rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div><b>{r.name || ROLE_NAMES[r.role]}</b> <span className="text-xs text-[#4A554E]">— {ROLE_NAMES[r.role]}{r.subjects ? ` (${r.subjects})` : ""}{r.phone ? ` — ${r.phone}` : " — لا يوجد رقم"}</span></div>
                    {sent && <Pill className="bg-emerald-100 text-emerald-800">✓ أُرسل</Pill>}
                  </div>
                  <details><summary className="text-xs text-[#4A554E] cursor-pointer">عرض نص الرسالة</summary>
                    <pre className="whitespace-pre-wrap text-xs bg-[#F9F8F6] rounded-lg p-2 mt-2 font-sans">{msg}</pre></details>
                  <div className="flex flex-wrap gap-2">
                    <a className={btnCls} target="_blank" rel="noopener noreferrer" onClick={() => markSent(r.id)}
                      href={`https://wa.me/${waNumber(r.phone)}?text=${encodeURIComponent(msg)}`}><MessageCircle className="w-4 h-4" /> واتساب</a>
                    <button className={btnGhost} onClick={() => { copyText(msg); markSent(r.id); }}><Copy className="w-4 h-4" /> نسخ الرسالة</button>
                    {r.token && <button className={btnGhost} onClick={() => copyText(linkOf(r.token))}><Copy className="w-4 h-4" /> نسخ الرابط فقط</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {mode === "raters" && (
          <RaterPicker contacts={contacts} teachers={teachers} setTeachers={setTeachers} deputies={deputies} setDeputies={setDeputies} activity={activity} setActivity={setActivity}>
            <button className={btnCls} onClick={saveRaters}>حفظ</button>
          </RaterPicker>
        )}

        {(c.violations || []).length > 0 && (
          <div>
            <div className="font-bold text-sm mb-1">مخالفات هذا الأسبوع (سجّلها الوكيل)</div>
            <Violations list={c.violations} />
          </div>
        )}
        {c.history?.length > 1 && <History history={c.history} />}

        {canManage && (
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#e6e2d8]">
            <button className={btnCls} onClick={close} data-testid="fu-close"><FileText className="w-4 h-4" /> إغلاق الأسبوع وإنشاء التقرير</button>
            <button className={`${btnGhost} text-red-700`} onClick={remove}><Trash2 className="w-4 h-4" /> حذف المتابعة</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------- إضافة طالب للمتابعة ----------
function StartModal({ students, contacts, active, onClose, onCreated }) {
  const aList = contacts.filter((u) => u.role === "activity");
  const [sid, setSid] = useState("");
  const [reason, setReason] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [deputies, setDeputies] = useState(contacts.filter((u) => u.role === "deputy").slice(0, 1).map((u) => u.id));
  const [activity, setActivity] = useState(aList.length === 1 ? aList[0].id : "");
  const [busy, setBusy] = useState(false);
  const free = students.filter((s) => !active.includes(s.id));
  const pick = (id) => {
    setSid(id);
    const s = students.find((x) => x.id === id);
    if (s) {
      const g = norm(s.grade);
      setTeachers(contacts.filter((u) => u.role === "teacher" && classTokens(u).some((tk) => g.includes(tk) || tk.includes(g))).map((u) => u.id));
    }
  };
  const save = async () => {
    setBusy(true);
    try { const r = await api.post("/followup/cycles", { student_id: sid, reason, teacher_ids: teachers, deputy_ids: deputies, activity_id: activity || null }); onCreated(r.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="إضافة طالب للمتابعة" onClose={onClose} wide>
      <div className="space-y-4" data-testid="fu-start">
        <Field label="الطالب"><StudentPicker students={free} value={sid} onChange={pick} /></Field>
        <Field label="سبب المتابعة"><input className={inputCls} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="مثال: كثرة الحركة والتأخر عن الطابور" data-testid="fu-reason" /></Field>
        <RaterPicker contacts={contacts} teachers={teachers} setTeachers={setTeachers} deputies={deputies} setDeputies={setDeputies} activity={activity} setActivity={setActivity}
          hint={sid ? "اخترنا تلقائيًا من يدرّس صف الطالب (إن كتبت الصفوف عند المعلم)، ويمكنك التعديل." : ""} />
        <button className={btnCls} disabled={!sid || busy} onClick={save} data-testid="fu-start-save"><Send className="w-4 h-4" /> حفظ وتجهيز الروابط</button>
      </div>
    </Modal>
  );
}

// ---------- المعلمون والوكيل ورائد النشاط (بلا حسابات) ----------
function ImportTeachers({ onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/followup/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(r.data.rows); setPick(Object.fromEntries(r.data.rows.map((x, i) => [i, !x.exists])));
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const edit = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const chosen = rows ? rows.filter((_, i) => pick[i]) : [];
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/followup/contacts/bulk", { role: "teacher", items: chosen.map(({ exists, ...x }) => x) });
      toast.success(`تمت إضافة ${r.data.added} معلم${r.data.skipped ? ` (تخطّينا ${r.data.skipped} مكرر)` : ""}`); onDone();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="استيراد المعلمين من ملف" onClose={onClose} wide>
      <div className="space-y-3" data-testid="fu-import">
        <p className="text-sm text-[#4A554E]">اختر ملف <b>Excel (.xlsx)</b> أو <b>PDF</b> فيه جدول بأعمدة: الاسم، المادة، الصفوف، الجوال، الإيميل. ستظهر لك معاينة تراجعها قبل الحفظ.</p>
        <input ref={fileRef} type="file" accept=".xlsx,.pdf" className={inputCls} disabled={busy} data-testid="fu-import-file" onChange={(e) => upload(e.target.files?.[0])} />
        {busy && !rows && <div className="text-sm">جارٍ قراءة الملف…</div>}
        {rows && (
          <>
            <div className="text-sm">وجدنا <b>{rows.length}</b> معلمًا. ألغِ علامة من لا تريده، ويمكنك تعديل أي خانة. (المكرر مُلغى تلقائيًا)</div>
            <div className="overflow-x-auto max-h-80 overflow-y-auto border border-[#e6e2d8] rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F8F6] sticky top-0"><tr>{["", "الاسم", "المادة", "الصفوف", "الجوال", "الإيميل"].map((h) => <th key={h} className="px-2 py-2 text-start font-semibold">{h}</th>)}</tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-[#f0ede4]">
                      <td className="px-2"><input type="checkbox" checked={!!pick[i]} onChange={(e) => setPick({ ...pick, [i]: e.target.checked })} /></td>
                      {["name", "subjects", "classes", "phone", "email"].map((k) => (
                        <td key={k} className="px-1 py-1"><input className="w-full min-w-[90px] rounded border border-[#e6e2d8] px-1.5 py-1" dir={k === "phone" || k === "email" ? "ltr" : "rtl"} value={r[k]} onChange={(e) => edit(i, k, e.target.value)} /></td>
                      ))}
                      {r.exists && <td className="px-2 text-xs text-amber-700 whitespace-nowrap">موجود مسبقًا</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className={btnCls} disabled={busy || !chosen.length} onClick={save} data-testid="fu-import-save">حفظ {chosen.length} معلم</button>
          </>
        )}
      </div>
    </Modal>
  );
}

function Contacts({ contacts, reload }) {
  const [edit, setEdit] = useState(null);
  const [importing, setImporting] = useState(false);
  const save = async () => {
    try {
      const body = { role: edit.role, name: edit.name, subjects: edit.subjects, classes: edit.classes, phone: edit.phone, email: edit.email || "" };
      if (edit.id) await api.put(`/followup/contacts/${edit.id}`, body); else await api.post("/followup/contacts", body);
      setEdit(null); reload(); toast.success("تم الحفظ");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`حذف ${u.name}؟`)) return;
    try { await api.delete(`/followup/contacts/${u.id}`); reload(); } catch (e) { toast.error(formatErr(e)); }
  };
  const row = (u) => (
    <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#f0ede4]">
      <div className="min-w-0">
        <div className="font-medium">{u.name}</div>
        <div className="text-xs text-[#4A554E]">{[u.subjects, u.classes, u.phone, u.email].filter(Boolean).join(" — ") || "لا توجد بيانات إضافية"}</div>
      </div>
      <div className="flex gap-1">
        <button className="p-1.5" title="تعديل" onClick={() => setEdit({ ...u })}><Pencil className="w-4 h-4" /></button>
        <button className="p-1.5 text-red-700" title="حذف" onClick={() => remove(u)}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
  const blank = (role) => ({ role, name: "", subjects: "", classes: "", phone: "", email: "" });
  const groups = [
    ["teacher", "معلمو المواد", "إضافة معلم", "لا يوجد معلمون", "اكتب الفصول التي يدرّسها كل معلم مثل: 8أ، 8ب، 9أ — ليختارهم البرنامج تلقائيًا عند متابعة طالب من تلك الفصول."],
    ["deputy", "الوكيل", "إضافة وكيل", "لم يُضف الوكيل بعد", ""],
    ["activity", "رائد النشاط", "إضافة رائد نشاط", "لم يُضف رائد النشاط بعد", ""],
  ];
  return (
    <div className="space-y-4">
      <div className="text-sm bg-white border border-[#e6e2d8] rounded-xl p-3">المعلمون والوكيل ورائد النشاط لا يحتاجون حسابات: يصلهم رابط خاص عبر واتساب يقيّمون منه. أضف رقم جوال كل واحد ليصله الرابط مباشرة.</div>
      {groups.map(([role, title, addLabel, emptyLabel, hint]) => {
        const list = contacts.filter((u) => u.role === role);
        return (
          <Card key={role} title={`${title} (${list.length})`} actions={<div className="flex flex-wrap gap-2">{role === "teacher" && <button className={btnGhost} onClick={() => setImporting(true)} data-testid="fu-import-open"><Upload className="w-4 h-4" /> استيراد من Excel أو PDF</button>}<button className={btnCls} onClick={() => setEdit(blank(role))} data-testid={`fu-add-${role}`}><Plus className="w-4 h-4" /> {addLabel}</button></div>}>
            {hint && <p className="text-sm text-[#4A554E] mb-2">{hint}</p>}
            {list.length ? list.map(row) : <Empty>{emptyLabel}</Empty>}
          </Card>
        );
      })}
      {importing && <ImportTeachers onClose={() => setImporting(false)} onDone={() => { setImporting(false); reload(); }} />}
      {edit && (
        <Modal title={edit.id ? "تعديل البيانات" : "إضافة"} onClose={() => setEdit(null)}>
          <div className="space-y-3" data-testid="fu-contact-form">
            <Field label="الاسم"><input className={inputCls} maxLength={100} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            {edit.role === "teacher" && <Field label="المواد"><input className={inputCls} value={edit.subjects} onChange={(e) => setEdit({ ...edit, subjects: e.target.value })} /></Field>}
            {edit.role === "teacher" && <Field label="الفصول التي يدرّسها"><input className={inputCls} value={edit.classes} placeholder="8أ، 8ب، 9أ" onChange={(e) => setEdit({ ...edit, classes: e.target.value })} /></Field>}
            <Field label="رقم الجوال"><input className={inputCls} dir="ltr" value={edit.phone} placeholder="05xxxxxxxx" onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></Field>
            <Field label="الإيميل (اختياري)"><input className={inputCls} dir="ltr" value={edit.email || ""} placeholder="name@example.com" onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></Field>
            <button className={btnCls} onClick={save} disabled={!edit.name.trim()}>حفظ</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- الشاشة الرئيسية للمرشد ----------
export default function StudentFollowUp() {
  const { user } = useAuth();
  const [sub, setSub] = useState("week");
  const [cycles, setCycles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(null);
  const [start, setStart] = useState(false);
  const [tick, setTick] = useState(0);
  const loadCycles = useCallback(() => { api.get("/followup/cycles").then((r) => setCycles(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  const loadContacts = useCallback(() => { api.get("/followup/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    loadCycles(); loadContacts();
    api.get("/records", { params: { type: "student_record" } })
      .then((r) => setStudents(r.data.map((x) => ({ id: x.id, name: x.data?.student_name || "", grade: x.data?.grade || "" }))))
      .catch(() => {});
  }, [loadCycles, loadContacts]);
  const done = cycles.reduce((a, c) => a + c.done, 0), exp = cycles.reduce((a, c) => a + c.expected, 0);
  const openCycle = (id) => api.get(`/followup/cycles/${id}`).then((r) => setOpen(r.data)).catch((e) => toast.error(formatErr(e)));
  const subs = [["week", "متابعة هذا الأسبوع", ClipboardCheck], ["reports", "التقارير", FileText], ["contacts", "المعلمون والوكيل والنشاط", Users]];
  return (
    <div className="space-y-4" dir="rtl">
      <div>
        <div className="text-sm text-[#3E6A56] font-medium mb-1">برنامج كيان</div>
        <h1 className="text-3xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>متابعة الطالب</h1>
        <p className="text-[#4A554E] mt-1">حدّد الطالب الذي يحتاج متابعة، وأرسل رابطًا خاصًا عبر واتساب للمعلمين والوكيل ورائد النشاط وولي الأمر ليقيّموه من A إلى D.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {subs.map(([k, l, Icon]) => <button key={k} className={sub === k ? btnCls : btnGhost} onClick={() => setSub(k)} data-testid={`fu-sub-${k}`}><Icon className="w-4 h-4" /> {l}</button>)}
      </div>
      {sub === "week" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="طلاب تحت المتابعة" value={cycles.length} />
            <StatCard label="اكتمال التقييمات" value={exp ? `${Math.round((done / exp) * 100)}%` : "—"} />
            <StatCard label="يحتاجون تدخلًا" value={cycles.filter((c) => c.pct != null && c.pct < 50).length} tone="text-red-700" />
          </div>
          <Card title="طلاب المتابعة هذا الأسبوع" actions={<button className={btnCls} onClick={() => setStart(true)} data-testid="fu-add"><Plus className="w-4 h-4" /> إضافة طالب للمتابعة</button>}>
            {cycles.length === 0 ? <Empty>لا يوجد طلاب تحت المتابعة هذا الأسبوع. اضغط «إضافة طالب للمتابعة».</Empty> : (
              <div className="divide-y divide-[#f0ede4]" data-testid="fu-cycles">
                {cycles.map((c) => (
                  <button key={c.id} onClick={() => openCycle(c.id)} className="w-full text-start flex flex-wrap items-center justify-between gap-3 py-3 px-2 rounded-lg hover:bg-[#F9F8F6]">
                    <div className="min-w-0">
                      <div className="font-medium">{c.student.name} <span className="text-xs text-[#4A554E]">{c.student.grade}</span></div>
                      <div className="text-xs text-[#4A554E]">{c.reason}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-28 h-1.5 bg-[#EEF3F2] rounded-full overflow-hidden"><div className="h-full bg-[#B08D3C]" style={{ width: `${c.expected ? (c.done / c.expected) * 100 : 0}%` }} /></div>
                        <span className="text-xs text-[#4A554E]">قيّم {c.done}/{c.expected}</span>
                      </div>
                    </div>
                    <GradePill pct={c.pct} />
                  </button>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
      {sub === "reports" && <ReportsList isAdmin={false} counselorName={user?.name || ""} refreshKey={tick} />}
      {sub === "contacts" && <Contacts contacts={contacts} reload={loadContacts} />}
      {start && <StartModal students={students} contacts={contacts} active={cycles.map((c) => c.student.id)} onClose={() => setStart(false)}
        onCreated={(c) => { setStart(false); loadCycles(); setTick((n) => n + 1); setOpen({ ...c, justCreated: true }); toast.success("أُضيف الطالب. أرسل الآن الروابط للمعلمين والوكيل ورائد النشاط وولي الأمر."); }} />}
      {open && <CycleModal cycle={open} contacts={contacts} counselorName={user?.name || ""} onClose={() => setOpen(null)} onChanged={() => { loadCycles(); setTick((n) => n + 1); }} />}
    </div>
  );
}
